import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { Breakpoint, debounce, PaletteMode, ThemeOptions, ThemeProvider as MuiThemeProvider, useMediaQuery } from '@mui/material';
import { Breakpoints } from '@mui/material/styles';
import createBreakpoints from '@mui/system/createTheme/createBreakpoints';
import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { useMeasure, useWindowSize } from 'react-use';
import { TssCacheProvider } from 'tss-react';
import { createSkodaTheme } from '../../theme';
import { defaultSkodaBreakpoints } from '../../theme/theme';

export const muiCache = createCache({
	'key': 'mui',
	'prepend': true
});

export const tssCache = createCache({
	'key': 'skoda'
});

export type ThemeProviderProps = {
	/** 
	 * Base theme override function.
	 */
	themeOverride?: (baseSkodaTheme: ThemeOptions) => ThemeOptions;

	/** 
	 * Breakpoints.
	 */
	breakpoints?: Breakpoints;

	/**
	 * Set true to make breakpoints dynamic relative to container.
	 */
	dynamicBreakpoints?: boolean;

	/**
	 * Use dark, light palette.
	 * Uses preferred mode if not defined
	 */
	mode?: PaletteMode;

	/**
	 * content
	 */
	children?: React.ReactNode;
};

/**
 * Skoda theme provider.
 */
export const ThemeProvider: React.FC<ThemeProviderProps> = (props) => {
	const { dynamicBreakpoints, ...other } = props;
	return dynamicBreakpoints
		? <ThemeProviderDynamicBreakpoints {...other} />
		: <ThemeProviderStaticBreakpoints {...other} />;
};

const ThemeProviderDynamicBreakpoints: React.FC<Omit<ThemeProviderProps, 'dynamicBreakpoints'>> = (props) => {
	const { themeOverride, breakpoints, children, mode } = props;
	const preferredMode = useMediaQuery('(prefers-color-scheme: dark)') ? 'dark' : 'light';
	const [relativeBreakpoints, setRelativeBreakpoints] = useState(breakpoints);
	const createdTheme = useMemo(() => createSkodaTheme(mode ?? preferredMode, themeOverride, relativeBreakpoints, breakpoints),
		[themeOverride, relativeBreakpoints, breakpoints, mode, preferredMode]);

	const [containerRef, { width: containerWidth }] = useMeasure();
	const { width: windowWidth } = useWindowSize();

	const handleResize = useCallback((window: number, container: number) => {
		const difference = window - container;
		const newValues = breakpoints.keys.reduce((prev, curr) => ({ ...prev, [curr]: breakpoints.values[curr] + difference }), {} as { [key in Breakpoint]: number });
		const newRelativeBreakpoints = createBreakpoints({ values: newValues });
		setRelativeBreakpoints(newRelativeBreakpoints);
	}, [setRelativeBreakpoints, breakpoints]);

	const debounceHandleResize = useMemo(() => debounce(handleResize, 250), [handleResize]);

	useLayoutEffect(() => {
		debounceHandleResize(windowWidth, containerWidth);
	}, [windowWidth, containerWidth, debounceHandleResize]);

	return (
		<div ref={containerRef} id='breakpoints-relative-div'>
			<CacheProvider value={muiCache}>
				<TssCacheProvider value={tssCache}>
					<MuiThemeProvider theme={createdTheme}>
						{children}
					</MuiThemeProvider>
				</TssCacheProvider>
			</CacheProvider>
		</div>
	);
};

const ThemeProviderStaticBreakpoints: React.FC<Omit<ThemeProviderProps, 'dynamicBreakpoints'>> = (props) => {
	const { themeOverride, breakpoints, children, mode } = props;
	const preferredMode = useMediaQuery('(prefers-color-scheme: dark)') ? 'dark' : 'light';
	const createdTheme = useMemo(() => createSkodaTheme(mode ?? preferredMode, themeOverride, breakpoints, breakpoints),
		[themeOverride, breakpoints, mode, preferredMode]);

	return (
		<CacheProvider value={muiCache}>
			<TssCacheProvider value={tssCache}>
				<MuiThemeProvider theme={createdTheme}>
					{children}
				</MuiThemeProvider>
			</TssCacheProvider>
		</CacheProvider>
	);
};

ThemeProvider.defaultProps = {
	breakpoints: defaultSkodaBreakpoints
};
